import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import "./ManageModule.css";
import { IoIosAddCircle } from "react-icons/io";
import { debounce } from "lodash";
import Notification from "../Notification/Notification";
import Display from "../ManageProgram/Display";
import AddModulePopup from "../ModuleCreator/AddModulePopup";
import {
  closestCorners,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import ModuleItem from "./Module.item";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // Importer useTranslation

function ManageModules(props) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [modules, setModules] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));
  const [error, setError] = useState("");
  const [notificationParams, setNotificationParams] = useState({
    show: false,
    message: t("notification_default_message"),
    bg: "success",
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (props.program) {
      setModules(props.program.modules);
      setModules((prev) =>
        prev.filter((mod) => {
          return mod.is_deleted === false;
        })
      );
    }
  }, [props.program]);

  if (!props.program) {
    return <div>{t("loading")}</div>;
  }

  const getModulePos = (id) => modules.findIndex((module) => module._id === id);

  const debouncedSaveModuleOrder = debounce((updatedModules) => {
    saveModuleOrder(updatedModules);
  }, 500);

  const saveModuleOrder = async (updatedModules) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/programs/${props.program._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ newModuleList: updatedModules }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("save_error"));
      }
    } catch (err) {
      console.error(t("error_captured"), err);
      setError(err.message || t("generic_error"));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setModules((prevModules) => {
      const originalPos = getModulePos(active.id);
      const newPos = getModulePos(over.id);
      const updatedModules = arrayMove(prevModules, originalPos, newPos);
      debouncedSaveModuleOrder(updatedModules);
      return updatedModules;
    });
  };

  const handleDeleteModule = async (moduleId) => {
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/modules/${moduleId}/delete`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      const data = await response.json();
      console.log("DATA :" + data.message);

      if (!response.ok) {
        throw new Error(data.message || t("generic_error"));
      }

      setModules((prev) =>
        prev.filter((module) => {
          return module._id !== moduleId;
        })
      );

      setNotificationParams((prev) => ({
        show: true,
        message: data.message,
        bg: "success",
      }));
    } catch (err) {
      setError(err.message);
      setNotificationParams((prev) => ({
        show: true,
        message: err.message,
        bg: "danger",
      }));
    }
  };

  if (!modules) {
    return <div>{t("loading")}</div>;
  }

  return (
    <div className="module-container shadow">
      <h2 className="text-center">{t("modules")}</h2>
      <p className="text-center text-muted">
        {t("manage_modules_description")}
      </p>

      {error && <div className="text-danger mt-2">{error}</div>}
      {modules.length === 0 && (
        <p className="text-center text-muted">{t("no_modules_yet")}</p>
      )}
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCorners}
      >
        <SortableContext items={modules} strategy={verticalListSortingStrategy}>
          {modules.map((module) => (
            <ModuleItem
              key={module._id}
              program_id={props.program._id}
              id={module._id}
              {...module}
              handleDelete={() => handleDeleteModule(module._id)}
            >
              {!module.sections || module.sections.length === 0 ? (
                <p className="text-center">{t("no_sections_yet")}</p>
              ) : (
                module.sections.map((section) => {
                  if (section.is_deleted === false) {
                    return (
                      <ModuleItem title={section.title} isSection={true}>
                        {!section.lessons || section.lessons.length === 0 ? (
                          <p className="text-center">{t("no_lessons_yet")}</p>
                        ) : (
                          section.lessons.map((lesson) => {
                            if (lesson.is_deleted === false) {
                              return (
                                <ModuleItem
                                  title={lesson.title}
                                  isLesson={true}
                                  lessonId={lesson._id}
                                  moduleId={module._id}
                                  program_id={props.program._id}
                                />
                              );
                            }
                          })
                        )}
                      </ModuleItem>
                    );
                  }
                })
              )}
            </ModuleItem>
          ))}
        </SortableContext>
      </DndContext>
      <AddModulePopup
        programId={props.program.id}
        setModules={setModules}
        modules={modules}
      />

      <Notification
        notificationParams={notificationParams}
        setNotificationParams={setNotificationParams}
      />
    </div>
  );
}

export default ManageModules;
